import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { buildGoogleFontsUrl } from './heroFonts';

// Scroll reveal hook (matches App.js implementation)
const useScrollReveal = (threshold = 0.1) => {
  const ref = useRef(null);
  const [isVisible, setIsVisible] = useState(false);
  useEffect(() => {
    const currentRef = ref.current;
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) setIsVisible(true); },
      { threshold }
    );
    if (currentRef) observer.observe(currentRef);
    return () => { if (currentRef) observer.unobserve(currentRef); };
  }, [threshold]);
  return [ref, isVisible];
};

// Responsive breakpoint hook
const useBreakpoint = () => {
  const [bp, setBp] = useState('desktop');
  useEffect(() => {
    const check = () => {
      const w = window.innerWidth;
      setBp(w < 768 ? 'mobile' : w < 1024 ? 'tablet' : 'desktop');
    };
    check();
    window.addEventListener('resize', check);
    return () => window.removeEventListener('resize', check);
  }, []);
  return bp;
};

// ===== LAYER ELEMENT RENDERER =====
const LayerElement = ({ layer }) => {
  const { type, content, x, y, width, height, rotation, zIndex, opacity, style = {} } = layer;

  const positionStyle = {
    position: 'absolute',
    left: `${x}%`,
    top: `${y}%`,
    transform: `translate(-50%, -50%) rotate(${rotation || 0}deg)`,
    zIndex: zIndex || 1,
    opacity: (opacity ?? 100) / 100,
    maxWidth: '90%',
  };

  const textStyle = {
    fontFamily: style.fontFamily || 'inherit',
    fontSize: style.fontSize || '16px',
    fontWeight: style.fontWeight || '400',
    color: style.color || '#D4B876',
    textAlign: style.textAlign || 'center',
    textShadow: style.textShadow === 'none' ? undefined : style.textShadow,
    letterSpacing: style.letterSpacing || 'normal',
    lineHeight: style.lineHeight || '1.4',
    textTransform: style.textTransform || 'none',
    whiteSpace: style.whiteSpace || 'nowrap',
  };

  if (width && width !== 'auto') {
    positionStyle.width = width;
    textStyle.whiteSpace = 'normal';
  }
  if (height && height !== 'auto') positionStyle.height = height;

  switch (type) {
    case 'heading':
      return (
        <h1
          style={{ ...positionStyle, ...textStyle }}
          data-testid={`hero-layer-${layer.id}`}
        >
          {content}
        </h1>
      );

    case 'text':
      return (
        <p
          style={{ ...positionStyle, ...textStyle }}
          data-testid={`hero-layer-${layer.id}`}
        >
          {content}
        </p>
      );

    case 'button': {
      const btnStyle = {
        ...positionStyle,
        ...textStyle,
        backgroundColor: style.backgroundColor || 'rgba(196,168,118,0.8)',
        padding: style.padding || '14px 36px',
        borderRadius: style.borderRadius || '4px',
        display: 'inline-block',
        textDecoration: 'none',
        cursor: 'pointer',
        transition: 'all 0.3s ease',
        border: style.border || 'none',
      };
      const linkTo = style.link || '#';
      if (linkTo.startsWith('http')) {
        return (
          <a href={linkTo} style={btnStyle} data-testid={`hero-layer-${layer.id}`}>
            {content}
          </a>
        );
      }
      return (
        <Link to={linkTo} style={btnStyle} data-testid={`hero-layer-${layer.id}`}>
          {content}
        </Link>
      );
    }

    case 'image':
    case 'decorative':
      if (!content) return null;
      return (
        <img
          src={content}
          alt=""
          style={{
            ...positionStyle,
            width: width && width !== 'auto' ? width : '200px',
            height: height && height !== 'auto' ? height : 'auto',
            objectFit: style.objectFit || 'contain',
            borderRadius: style.borderRadius || '0px',
            pointerEvents: type === 'decorative' ? 'none' : 'auto',
          }}
          data-testid={`hero-layer-${layer.id}`}
        />
      );

    default:
      return null;
  }
};

// ===== NEW LAYERED HERO RENDERER =====
const LayeredHero = ({ settings = {}, layers = [] }) => {
  const [ref, isVisible] = useScrollReveal(0.1);
  const breakpoint = useBreakpoint();

  // Load fonts used in layers
  useEffect(() => {
    const usedFonts = [...new Set(layers.map(l => l.style?.fontFamily).filter(Boolean))];
    if (usedFonts.length) {
      const existingLink = document.querySelector('link[data-hero-layer-fonts]');
      if (existingLink) existingLink.remove();
      const link = document.createElement('link');
      link.rel = 'stylesheet';
      link.href = buildGoogleFontsUrl(usedFonts);
      link.setAttribute('data-hero-layer-fonts', 'true');
      document.head.appendChild(link);
      return () => { if (link.parentNode) link.remove(); };
    }
  }, [layers]);

  const heightMap = {
    desktop: settings.height_desktop || '100vh',
    tablet: settings.height_tablet || '80vh',
    mobile: settings.height_mobile || '70vh',
  };

  const sortedLayers = useMemo(() =>
    [...layers]
      .filter(l => l.visible !== false)
      .sort((a, b) => (a.zIndex || 0) - (b.zIndex || 0)),
    [layers]
  );

  return (
    <section
      ref={ref}
      className="relative overflow-hidden"
      style={{ height: heightMap[breakpoint] }}
      data-testid="hero-section"
    >
      {/* Background Image */}
      {settings.background_image && (
        <div className="absolute inset-0">
          <img
            src={settings.background_image}
            alt=""
            className="w-full h-full object-cover"
            style={{
              objectPosition: settings.background_position || 'center center',
              filter: settings.background_blur ? `blur(${settings.background_blur}px)` : undefined,
            }}
          />
        </div>
      )}

      {/* Background Video */}
      {settings.background_video && !settings.background_image && (
        <video
          className="absolute inset-0 w-full h-full object-cover"
          autoPlay muted loop playsInline
          style={{ filter: settings.background_blur ? `blur(${settings.background_blur}px)` : undefined }}
        >
          <source src={settings.background_video} type="video/mp4" />
        </video>
      )}

      {/* Overlay */}
      <div
        className="absolute inset-0 pointer-events-none"
        style={{
          backgroundColor: settings.background_overlay_color || '#000',
          opacity: (settings.background_overlay_opacity ?? 30) / 100,
        }}
      />

      {/* Layers */}
      <div
        className={`relative w-full h-full transition-all duration-[2000ms] ${
          isVisible ? 'opacity-100' : 'opacity-0 translate-y-4'
        }`}
      >
        {sortedLayers.map(layer => (
          <LayerElement key={layer.id} layer={layer} />
        ))}
      </div>
    </section>
  );
};

// ===== LEGACY HERO RENDERER (backwards compat) =====
const LegacyHero = ({ content }) => {
  const [ref, isVisible] = useScrollReveal(0.1);
  const verticalOffset = content.vertical_offset ?? 0;
  const showGradient = content.show_gradient !== false;

  return (
    <section ref={ref} className="relative min-h-screen overflow-hidden" data-testid="hero-section">
      <div className="absolute inset-0">
        <img
          src={content.background_image || "https://customer-assets.emergentagent.com/job_cinematic-reveal-1/artifacts/f4hqb2ww_FD567340-8D98-47C1-A961-49662CE54C8B.png"}
          alt="Hero background"
          className="w-full h-full object-cover hero-image-position"
        />
        {showGradient && (
          <>
            <div className="absolute inset-0 bg-gradient-to-t from-charcoal-900/40 via-transparent to-charcoal-900/10" />
            <div className="absolute inset-0 bg-gradient-to-b from-charcoal-900/15 via-transparent to-transparent" />
          </>
        )}
        <div className="absolute inset-0" style={{background: 'radial-gradient(ellipse 65% 55% at 50% 42%, rgba(0,0,0,0.35) 0%, transparent 75%)'}} />
      </div>
      <div
        className={`absolute z-10 inset-0 flex flex-col items-center justify-center text-center px-5 pt-[70px] md:pt-[90px] transition-all duration-[2000ms] ${
          isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
        }`}
      >
        <div className="relative max-w-[700px] mx-auto" style={{ marginTop: `${verticalOffset}px` }}>
          <div className="absolute inset-0 -inset-x-12 -inset-y-8 pointer-events-none" style={{background: 'radial-gradient(ellipse 80% 90% at 50% 45%, rgba(0,0,0,0.12) 0%, transparent 70%)'}} />
          <div className="relative">
            <p className="font-serif text-gold-400/80 tracking-[0.4em] text-[10px] md:text-xs mb-4 uppercase" style={{textShadow: '0 0 6px rgba(212,184,118,0.3), 0 1px 3px rgba(0,0,0,0.5)'}}>
              {content.welcome_text || 'Welcome to'}
            </p>
            <h1 className="hero-title-glow text-4xl sm:text-5xl md:text-7xl lg:text-8xl mb-3 md:mb-5 leading-[1.05]" data-testid="hero-title">
              {content.main_title || 'The Timeless Prophecy'}
            </h1>
            <p className="font-serif text-gold-300/80 text-xs md:text-sm tracking-[0.2em] mb-6" data-testid="hero-subtitle" style={{textShadow: '0 0 8px rgba(212,184,118,0.25), 0 1px 4px rgba(0,0,0,0.6)'}}>
              {content.subtitle || 'An Immersive Storytelling Experience'}
            </p>
            <p className="font-serif text-sm md:text-base text-gold-300/85 leading-relaxed max-w-[480px] mx-auto" data-testid="hero-description" style={{textShadow: '0 0 8px rgba(212,184,118,0.3), 0 1px 4px rgba(0,0,0,0.5)'}}>
              {content.description || ''}
            </p>
          </div>
        </div>
        {content.cta_text && (
          <div className="mt-6 md:mt-12 flex flex-col sm:flex-row gap-3 items-center justify-center w-full px-4 sm:px-0" data-testid="hero-cta-wrapper">
            <Link
              to={content.cta_link || '/membership'}
              data-testid="hero-cta-button"
              className="inline-block w-full sm:w-auto text-center px-8 py-3 md:py-3.5 bg-gradient-to-r from-gold-600 to-gold-500 hover:from-gold-500 hover:to-gold-400 text-parchment-100 font-display text-xs tracking-widest uppercase rounded transition-all duration-300 shadow-lg hover:shadow-gold-600/20"
            >
              {content.cta_text}
            </Link>
          </div>
        )}
      </div>
    </section>
  );
};

// ===== MAIN HERO RENDERER =====
const HeroRenderer = ({ content }) => {
  const isLayered = content?.layers && Array.isArray(content.layers);

  if (!isLayered) {
    return <LegacyHero content={content || {}} />;
  }

  return <LayeredHero settings={content.settings} layers={content.layers} />;
};

export default HeroRenderer;
